/**
 * AudioStorageService - IndexedDB-based audio file storage
 *
 * Provides persistent storage for session audio recordings using IndexedDB,
 * which can handle much larger files than localStorage.
 *
 * Features:
 * - Store original and filtered audio blobs
 * - Retrieve audio for playback
 * - Delete audio files
 * - Replace existing audio
 * - Storage quota management
 * - Access logging
 */

export interface AudioMetadata {
  id: string;
  sessionId: string;
  patientId?: string;
  originalBlobSize: number;
  filteredBlobSize: number;
  duration: number;
  sampleRate: number;
  channels: number;
  mimeType: string;
  createdAt: Date;
  updatedAt: Date;
  accessCount: number;
  lastAccessedAt?: Date;
  tags: string[];
}

export interface StoredAudio {
  metadata: AudioMetadata;
  originalBlob: Blob;
  filteredBlob: Blob;
}

interface AccessLogEntry {
  timestamp: Date;
  userId: string;
  action: 'created' | 'accessed' | 'downloaded' | 'deleted' | 'replaced';
}

class AudioStorageService {
  private dbName = 'ClinicCoreAudioDB';
  private dbVersion = 1;
  private audioStoreName = 'audioRecordings';
  private metadataStoreName = 'audioMetadata';
  private accessLogStoreName = 'accessLogs';
  private db: IDBDatabase | null = null;

  /**
   * Initialize IndexedDB
   */
  async initialize(): Promise<boolean> {
    if (this.db) {
      return true; // Already initialized
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(new Error('Failed to initialize audio storage'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('AudioStorageService initialized successfully');
        resolve(true);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create audio recordings store (stores actual blobs)
        if (!db.objectStoreNames.contains(this.audioStoreName)) {
          const audioStore = db.createObjectStore(this.audioStoreName, { keyPath: 'id' });
          audioStore.createIndex('sessionId', 'sessionId', { unique: false });
          audioStore.createIndex('patientId', 'patientId', { unique: false });
          audioStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Create metadata store (for quick queries without loading blobs)
        if (!db.objectStoreNames.contains(this.metadataStoreName)) {
          const metadataStore = db.createObjectStore(this.metadataStoreName, { keyPath: 'id' });
          metadataStore.createIndex('sessionId', 'sessionId', { unique: false });
          metadataStore.createIndex('patientId', 'patientId', { unique: false });
          metadataStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Create access log store
        if (!db.objectStoreNames.contains(this.accessLogStoreName)) {
          const logStore = db.createObjectStore(this.accessLogStoreName, { autoIncrement: true });
          logStore.createIndex('audioId', 'audioId', { unique: false });
          logStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        console.log('IndexedDB schema created');
      };
    });
  }

  /**
   * Save audio recording to IndexedDB
   */
  async saveAudio(
    sessionId: string,
    originalBlob: Blob,
    filteredBlob: Blob,
    metadata: {
      patientId?: string;
      duration: number;
      sampleRate: number;
      channels: number;
    }
  ): Promise<string> {
    await this.ensureInitialized();

    const audioId = this.generateAudioId(sessionId);

    const audioMetadata: AudioMetadata = {
      id: audioId,
      sessionId,
      patientId: metadata.patientId,
      originalBlobSize: originalBlob.size,
      filteredBlobSize: filteredBlob.size,
      duration: metadata.duration,
      sampleRate: metadata.sampleRate,
      channels: metadata.channels,
      mimeType: originalBlob.type || 'audio/webm',
      createdAt: new Date(),
      updatedAt: new Date(),
      accessCount: 0,
      tags: this.generateTags(sessionId, metadata.patientId)
    };

    const storedAudio = {
      id: audioId,
      sessionId,
      patientId: metadata.patientId,
      originalBlob,
      filteredBlob,
      createdAt: new Date()
    };

    try {
      // Store audio blobs
      await this.putInStore(this.audioStoreName, storedAudio);

      // Store metadata separately for fast queries
      await this.putInStore(this.metadataStoreName, audioMetadata);

      // Log creation
      await this.logAccess(audioId, 'current_user', 'created');

      console.log(`Audio saved: ${audioId} (Original: ${this.formatBytes(originalBlob.size)}, Filtered: ${this.formatBytes(filteredBlob.size)})`);

      return audioId;
    } catch (error) {
      console.error('Failed to save audio:', error);
      throw new Error('Failed to save audio recording');
    }
  }

  /**
   * Get audio by session ID
   */
  async getAudioBySessionId(sessionId: string): Promise<StoredAudio | null> {
    await this.ensureInitialized();

    try {
      const metadata = await this.getMetadataBySessionId(sessionId);
      if (!metadata) {
        return null;
      }

      const audioData = await this.getFromStore(this.audioStoreName, metadata.id);
      if (!audioData) {
        return null;
      }

      // Update access count and timestamp
      metadata.accessCount++;
      metadata.lastAccessedAt = new Date();
      await this.putInStore(this.metadataStoreName, metadata);

      // Log access
      await this.logAccess(metadata.id, 'current_user', 'accessed');

      return {
        metadata,
        originalBlob: audioData.originalBlob,
        filteredBlob: audioData.filteredBlob
      };
    } catch (error) {
      console.error('Failed to get audio:', error);
      return null;
    }
  }

  /**
   * Get audio by audio ID
   */
  async getAudio(audioId: string): Promise<StoredAudio | null> {
    await this.ensureInitialized();

    try {
      const metadata = await this.getFromStore(this.metadataStoreName, audioId);
      if (!metadata) {
        return null;
      }

      const audioData = await this.getFromStore(this.audioStoreName, audioId);
      if (!audioData) {
        return null;
      }

      // Update access count and timestamp
      metadata.accessCount++;
      metadata.lastAccessedAt = new Date();
      await this.putInStore(this.metadataStoreName, metadata);

      // Log access
      await this.logAccess(audioId, 'current_user', 'accessed');

      return {
        metadata,
        originalBlob: audioData.originalBlob,
        filteredBlob: audioData.filteredBlob
      };
    } catch (error) {
      console.error('Failed to get audio:', error);
      return null;
    }
  }

  /**
   * Get playback URL for audio (creates object URL)
   */
  async getAudioUrl(sessionId: string, type: 'original' | 'filtered'): Promise<string | null> {
    const audio = await this.getAudioBySessionId(sessionId);
    if (!audio) {
      return null;
    }

    const blob = type === 'original' ? audio.originalBlob : audio.filteredBlob;
    return URL.createObjectURL(blob);
  }

  /**
   * Replace existing audio
   */
  async replaceAudio(
    sessionId: string,
    newOriginalBlob: Blob,
    newFilteredBlob: Blob
  ): Promise<boolean> {
    await this.ensureInitialized();

    try {
      const metadata = await this.getMetadataBySessionId(sessionId);
      if (!metadata) {
        console.error('Audio not found for session:', sessionId);
        return false;
      }

      // Update metadata
      metadata.originalBlobSize = newOriginalBlob.size;
      metadata.filteredBlobSize = newFilteredBlob.size;
      metadata.updatedAt = new Date();

      // Update audio data
      const audioData = {
        id: metadata.id,
        sessionId,
        patientId: metadata.patientId,
        originalBlob: newOriginalBlob,
        filteredBlob: newFilteredBlob,
        createdAt: metadata.createdAt
      };

      await this.putInStore(this.audioStoreName, audioData);
      await this.putInStore(this.metadataStoreName, metadata);

      // Log replacement
      await this.logAccess(metadata.id, 'current_user', 'replaced');

      console.log(`Audio replaced for session: ${sessionId}`);
      return true;
    } catch (error) {
      console.error('Failed to replace audio:', error);
      return false;
    }
  }

  /**
   * Delete audio
   */
  async deleteAudio(sessionId: string): Promise<boolean> {
    await this.ensureInitialized();

    try {
      const metadata = await this.getMetadataBySessionId(sessionId);
      if (!metadata) {
        return false;
      }

      // Log deletion before removing
      await this.logAccess(metadata.id, 'current_user', 'deleted');

      // Delete from both stores
      await this.deleteFromStore(this.audioStoreName, metadata.id);
      await this.deleteFromStore(this.metadataStoreName, metadata.id);

      console.log(`Audio deleted for session: ${sessionId}`);
      return true;
    } catch (error) {
      console.error('Failed to delete audio:', error);
      return false;
    }
  }

  /**
   * Get all audio metadata (without loading blobs)
   */
  async getAllMetadata(): Promise<AudioMetadata[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.metadataStoreName], 'readonly');
      const store = transaction.objectStore(this.metadataStoreName);
      const request = store.getAll();

      request.onsuccess = () => {
        const metadata = request.result.map((m: any) => ({
          ...m,
          createdAt: new Date(m.createdAt),
          updatedAt: new Date(m.updatedAt),
          lastAccessedAt: m.lastAccessedAt ? new Date(m.lastAccessedAt) : undefined
        }));
        resolve(metadata);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get audio metadata by patient ID
   */
  async getMetadataByPatientId(patientId: string): Promise<AudioMetadata[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.metadataStoreName], 'readonly');
      const store = transaction.objectStore(this.metadataStoreName);
      const index = store.index('patientId');
      const request = index.getAll(patientId);

      request.onsuccess = () => {
        const metadata = request.result.map((m: any) => ({
          ...m,
          createdAt: new Date(m.createdAt),
          updatedAt: new Date(m.updatedAt),
          lastAccessedAt: m.lastAccessedAt ? new Date(m.lastAccessedAt) : undefined
        }));
        resolve(metadata);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get storage usage statistics
   */
  async getStorageStats(): Promise<{
    totalFiles: number;
    totalSize: number;
    originalSize: number;
    filteredSize: number;
    averageCompression: number;
    oldestFile?: Date;
    newestFile?: Date;
  }> {
    const allMetadata = await this.getAllMetadata();

    if (allMetadata.length === 0) {
      return {
        totalFiles: 0,
        totalSize: 0,
        originalSize: 0,
        filteredSize: 0,
        averageCompression: 0
      };
    }

    const totalOriginalSize = allMetadata.reduce((sum, m) => sum + m.originalBlobSize, 0);
    const totalFilteredSize = allMetadata.reduce((sum, m) => sum + m.filteredBlobSize, 0);
    const totalSize = totalOriginalSize + totalFilteredSize;

    const sortedByDate = allMetadata.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

    return {
      totalFiles: allMetadata.length,
      totalSize,
      originalSize: totalOriginalSize,
      filteredSize: totalFilteredSize,
      averageCompression: totalOriginalSize > 0 ? ((totalOriginalSize - totalFilteredSize) / totalOriginalSize) * 100 : 0,
      oldestFile: sortedByDate[0]?.createdAt,
      newestFile: sortedByDate[sortedByDate.length - 1]?.createdAt
    };
  }

  /**
   * Download audio file
   */
  async downloadAudio(
    sessionId: string,
    type: 'original' | 'filtered',
    options?: {
      clientName?: string;
      sessionDate?: Date;
      sessionType?: string;
    }
  ): Promise<void> {
    const audio = await this.getAudioBySessionId(sessionId);
    if (!audio) {
      console.error('Audio not found for session:', sessionId);
      return;
    }

    const blob = type === 'original' ? audio.originalBlob : audio.filteredBlob;
    const url = URL.createObjectURL(blob);

    // Generate filename using FileNamingService if client info provided
    let filename: string;
    if (options?.clientName && options?.sessionDate && options?.sessionType) {
      const FileNamingService = require('./FileNamingService').default;
      const namingService = FileNamingService.getInstance();

      if (type === 'original') {
        filename = namingService.generateSessionAudioOriginalFilename({
          clientName: options.clientName,
          sessionDate: options.sessionDate,
          sessionType: options.sessionType,
          fileExtension: this.getFileExtension(blob.type)
        });
      } else {
        filename = namingService.generateSessionAudioEditedFilename({
          clientName: options.clientName,
          sessionDate: options.sessionDate,
          sessionType: options.sessionType,
          fileExtension: this.getFileExtension(blob.type)
        });
      }
    } else {
      // Fallback to old format if no client info provided
      filename = `${sessionId}_${type}_audio.${this.getFileExtension(blob.type)}`;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Log download
    await this.logAccess(audio.metadata.id, 'current_user', 'downloaded');
  }

  /**
   * Clear all audio storage (use with caution!)
   */
  async clearAllAudio(): Promise<boolean> {
    await this.ensureInitialized();

    try {
      await this.clearStore(this.audioStoreName);
      await this.clearStore(this.metadataStoreName);
      await this.clearStore(this.accessLogStoreName);
      console.log('All audio storage cleared');
      return true;
    } catch (error) {
      console.error('Failed to clear audio storage:', error);
      return false;
    }
  }

  // Private helper methods

  private async ensureInitialized(): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }
  }

  private generateAudioId(sessionId: string): string {
    return `audio_${sessionId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateTags(sessionId: string, patientId?: string): string[] {
    const tags = ['audio'];
    if (patientId) {
      tags.push('patient-session');
    }
    const hour = new Date().getHours();
    tags.push(hour >= 9 && hour <= 17 ? 'business-hours' : 'after-hours');
    return tags;
  }

  private async getMetadataBySessionId(sessionId: string): Promise<AudioMetadata | null> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.metadataStoreName], 'readonly');
      const store = transaction.objectStore(this.metadataStoreName);
      const index = store.index('sessionId');
      const request = index.get(sessionId);

      request.onsuccess = () => {
        if (request.result) {
          const metadata = {
            ...request.result,
            createdAt: new Date(request.result.createdAt),
            updatedAt: new Date(request.result.updatedAt),
            lastAccessedAt: request.result.lastAccessedAt ? new Date(request.result.lastAccessedAt) : undefined
          };
          resolve(metadata);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  private async putInStore(storeName: string, data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async getFromStore(storeName: string, key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async deleteFromStore(storeName: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async clearStore(storeName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async logAccess(audioId: string, userId: string, action: AccessLogEntry['action']): Promise<void> {
    const logEntry = {
      audioId,
      userId,
      action,
      timestamp: new Date()
    };

    try {
      await this.putInStore(this.accessLogStoreName, logEntry);
    } catch (error) {
      console.error('Failed to log access:', error);
      // Don't throw - logging failure shouldn't break main functionality
    }
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  private getFileExtension(mimeType: string): string {
    const mimeMap: Record<string, string> = {
      'audio/webm': 'webm',
      'audio/wav': 'wav',
      'audio/mp3': 'mp3',
      'audio/mpeg': 'mp3',
      'audio/ogg': 'ogg'
    };
    return mimeMap[mimeType] || 'webm';
  }
}

// Export singleton instance
export const audioStorageService = new AudioStorageService();
export default AudioStorageService;
